/**
 * SharpSportsSection Component
 * Sportsbook integration via SharpSports - account linking, bet import, reconciliation
 */

import { getSharpSportsSportsbooks, type SportsbookConfig } from '@config/constants';
import { useSharpSports } from '@hooks/useSharpSports';
import { cn } from '@utils/cn';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, CheckCircle, Info, RefreshCw, X } from 'lucide-react';
import type { ReactElement } from 'react';
import { useCallback, useState } from 'react';
import { LinkedAccountCard } from './LinkedAccountCard';

// =============================================================================
// Constants
// =============================================================================

const CHROME_EXTENSION_URL =
  import.meta.env.VITE_CHROME_EXTENSION_URL ||
  'https://chromewebstore.google.com/detail/sharpsports/jbjkingkpiakebpokhndnnmniaafoobe';

const SUPPORTED_SPORTSBOOKS = getSharpSportsSportsbooks();

// =============================================================================
// Component
// =============================================================================

interface SharpSportsSectionProps {
  // Handlers
  onActionMessage: (message: { type: 'success' | 'error'; text: string }) => void;
}

export function SharpSportsSection({ onActionMessage }: SharpSportsSectionProps): ReactElement {
  const {
    status,
    linkedAccounts,
    importedBets,
    browserExtension,
    loading,
    linkingInProgress,
    linkAccount,
    refreshBets,
    processBets,
    reconcileBets,
    checkBrowserExtension,
  } = useSharpSports();

  const [showLinkFlow, setShowLinkFlow] = useState(false);
  const [selectedSportsbook, setSelectedSportsbook] = useState<SportsbookConfig | null>(null);
  const [syncing, setSyncing] = useState(false);

  const handleStartLinkFlow = useCallback((): void => {
    setShowLinkFlow(true);
    setSelectedSportsbook(null);
    // Recheck extension status when opening the flow
    checkBrowserExtension();
  }, [checkBrowserExtension]);

  const handleSelectSportsbook = useCallback((sportsbook: SportsbookConfig): void => {
    setSelectedSportsbook(sportsbook);
  }, []);

  const handleProceedWithLink = useCallback(async (): Promise<void> => {
    if (!selectedSportsbook) return;

    // If the sportsbook requires extension and it's not installed, show install prompt
    if (selectedSportsbook.requiresExtension && !browserExtension.isInstalled) {
      // Don't proceed - the UI will show the install prompt
      return;
    }

    const linkUrl = await linkAccount(`${window.location.origin}/settings`);
    if (linkUrl) {
      // Open SharpSports linking UI in a new tab
      window.open(linkUrl, '_blank');
      onActionMessage({
        type: 'success',
        text: `Link opened in new tab. Complete the ${selectedSportsbook.name} linking process there, then return here.`,
      });
      setShowLinkFlow(false);
      setSelectedSportsbook(null);
    }
  }, [selectedSportsbook, browserExtension.isInstalled, linkAccount, onActionMessage]);

  const handleInstallExtension = useCallback((): void => {
    window.open(CHROME_EXTENSION_URL, '_blank');
    onActionMessage({
      type: 'success',
      text: "Install the extension, then click 'I've Installed It' to continue.",
    });
  }, [onActionMessage]);

  const handleCheckExtensionInstalled = useCallback((): void => {
    checkBrowserExtension();
  }, [checkBrowserExtension]);

  const handleSyncAll = useCallback(async (): Promise<void> => {
    setSyncing(true);
    try {
      const refreshResult = await refreshBets();
      if (!refreshResult.success) {
        onActionMessage({ type: 'error', text: refreshResult.message });
        return;
      }
      const processResult = await processBets();
      const reconcileResult = await reconcileBets();
      onActionMessage({
        type: 'success',
        text: `Synced: fetched bets, processed ${processResult.processed}, matched ${reconcileResult.matched} to tracked parlays.`,
      });
    } finally {
      setSyncing(false);
    }
  }, [refreshBets, processBets, reconcileBets, onActionMessage]);

  return (
    <section className="mb-6">
      <h3 className="text-lg font-medium text-theme-text mb-2">Sportsbook Integration</h3>
      <p className="text-sm text-theme-text-secondary mb-4">
        Connect your sportsbook accounts to automatically import bets and verify your tracked
        parlays.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-theme-text-secondary">
          <div className="w-5 h-5 border-2 border-theme-border border-t-transparent rounded-full animate-spin" />
          <span>Loading...</span>
        </div>
      ) : !status?.enabled ? (
        <div className="p-4 bg-theme-surface rounded-lg border border-theme-border">
          <p className="text-theme-text-secondary">
            Sportsbook integration is not currently enabled. Contact support to enable this feature.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Linked Accounts */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-theme-text-secondary">Linked Accounts</h4>
              {!showLinkFlow && (
                <button
                  type="button"
                  onClick={handleStartLinkFlow}
                  disabled={linkingInProgress}
                  className="px-3 py-1.5 bg-theme-primary-fill hover:bg-theme-primary-fill-hover disabled:bg-theme-surface disabled:text-theme-text-tertiary text-theme-on-primary-fill text-sm font-medium rounded-lg transition-colors"
                >
                  {linkingInProgress ? 'Linking...' : '+ Link Account'}
                </button>
              )}
            </div>

            {/* Link Flow - Sportsbook Selection */}
            <AnimatePresence initial={false}>
              {showLinkFlow && (
                <motion.div
                  key="link-flow"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.25, ease: 'easeInOut' }}
                  className="overflow-hidden"
                >
                  <div className="p-4 bg-theme-surface rounded-lg border border-theme-border mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <h5 className="font-medium text-theme-text">Select Your Sportsbook</h5>
                      <button
                        type="button"
                        onClick={() => {
                          setShowLinkFlow(false);
                          setSelectedSportsbook(null);
                        }}
                        className="text-theme-text-tertiary hover:text-theme-text transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Sportsbook Grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
                      {SUPPORTED_SPORTSBOOKS.map((sportsbook) => (
                        <button
                          type="button"
                          key={sportsbook.id}
                          onClick={() => handleSelectSportsbook(sportsbook)}
                          className={`p-3 rounded-lg border text-left transition-colors ${
                            selectedSportsbook?.id === sportsbook.id
                              ? 'bg-theme-primary/20 border-theme-primary text-theme-text'
                              : 'bg-theme-bg border-theme-border text-theme-text-secondary hover:bg-theme-surface hover:border-theme-text-tertiary'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${sportsbook.dot}`} />
                            <span className="font-medium text-sm">{sportsbook.name}</span>
                          </div>
                          {sportsbook.requiresExtension && (
                            <div
                              className={cn(
                                'text-xs mt-1 flex items-center gap-1 ml-4',
                                'text-theme-warning',
                              )}
                            >
                              <Info className="w-3 h-3" />
                              Extension required
                            </div>
                          )}
                        </button>
                      ))}
                    </div>

                    {/* Extension Install Prompt - shows when selected sportsbook requires extension */}
                    {selectedSportsbook?.requiresExtension && !browserExtension.isInstalled && (
                      <div
                        className={cn(
                          'p-4 rounded-lg mb-4 border',
                          'bg-theme-warning/10 border-theme-warning/30',
                        )}
                      >
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="w-5 h-5" />
                          <div className="flex-1">
                            <h6 className={cn('font-medium mb-1', 'text-theme-warning')}>
                              Browser Extension Required
                            </h6>
                            <p className={cn('text-sm mb-3', 'text-theme-warning/80')}>
                              {selectedSportsbook.name} requires the SharpSports browser extension
                              to securely link your account. The extension reads your login session
                              from the browser - you must be logged into {selectedSportsbook.name}{' '}
                              for it to work.
                            </p>
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={handleInstallExtension}
                                className={cn(
                                  'px-3 py-1.5 text-white text-sm font-medium rounded transition-colors',
                                  'bg-theme-warning hover:bg-theme-warning-hover',
                                )}
                              >
                                Install Extension
                              </button>
                              <button
                                type="button"
                                onClick={handleCheckExtensionInstalled}
                                className={cn(
                                  'px-3 py-1.5 text-white text-sm font-medium rounded transition-colors',
                                  'bg-theme-surface hover:bg-theme-surface-hover',
                                )}
                              >
                                I&apos;ve Installed It
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Extension Installed Success */}
                    {selectedSportsbook?.requiresExtension && browserExtension.isInstalled && (
                      <div
                        className={cn(
                          'p-3 rounded-lg mb-4 border',
                          'bg-theme-success/10 border-theme-success/30',
                        )}
                      >
                        <div className={cn('flex items-center gap-2', 'text-theme-success')}>
                          <CheckCircle className="w-5 h-5" />
                          <span className="text-sm font-medium">
                            Extension installed (v{browserExtension.version})
                          </span>
                        </div>
                        <p className={cn('text-sm mt-1 ml-7', 'text-theme-success/70')}>
                          Make sure you&apos;re logged into {selectedSportsbook.name} in this
                          browser before continuing.
                        </p>
                      </div>
                    )}

                    {/* Proceed Button */}
                    {selectedSportsbook && (
                      <button
                        type="button"
                        onClick={handleProceedWithLink}
                        disabled={
                          linkingInProgress ||
                          (selectedSportsbook.requiresExtension && !browserExtension.isInstalled)
                        }
                        className="w-full px-4 py-2 bg-theme-primary-fill hover:bg-theme-primary-fill-hover disabled:bg-theme-surface disabled:text-theme-text-tertiary text-theme-on-primary-fill font-medium rounded-lg transition-colors"
                      >
                        {linkingInProgress ? 'Linking...' : `Link ${selectedSportsbook.name}`}
                      </button>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {linkedAccounts.length === 0 && !showLinkFlow ? (
              <div className="p-4 bg-theme-surface rounded-lg border border-theme-border text-center">
                <p className="text-theme-text-secondary mb-2">No sportsbook accounts linked yet.</p>
                <p className="text-sm text-theme-text-tertiary">
                  Click &quot;Link Account&quot; to connect your sportsbook.
                </p>
              </div>
            ) : linkedAccounts.length > 0 ? (
              <div className="grid gap-3">
                {linkedAccounts.map((account) => (
                  <LinkedAccountCard
                    key={account.id}
                    sportsbook={account.sportsbook}
                    sportsbookName={account.sportsbookName}
                    status={account.status}
                    linkedAt={account.linkedAt}
                    lastRefreshAt={account.lastRefreshAt}
                  />
                ))}
              </div>
            ) : null}
          </div>

          {/* Actions - only show if accounts are linked */}
          {linkedAccounts.length > 0 && (
            <div className="pt-4 border-t border-theme-border">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleSyncAll}
                  disabled={syncing}
                  className="flex items-center gap-2 px-4 py-2 bg-theme-primary-fill hover:bg-theme-primary-fill-hover disabled:bg-theme-surface disabled:text-theme-text-tertiary text-theme-on-primary-fill text-sm font-medium rounded-lg transition-colors"
                >
                  <RefreshCw className={cn('w-4 h-4', syncing && 'animate-spin')} />
                  {syncing ? 'Syncing...' : 'Sync Now'}
                </button>
                {(() => {
                  const mostRecentRefresh = linkedAccounts
                    .map((a) => a.lastRefreshAt)
                    .filter((d): d is string => d !== null)
                    .sort()
                    .at(-1);
                  if (!mostRecentRefresh) return null;
                  const diffMs = Date.now() - new Date(mostRecentRefresh).getTime();
                  const diffMin = Math.floor(diffMs / 60000);
                  const timeAgo =
                    diffMin < 1
                      ? 'just now'
                      : diffMin === 1
                        ? '1 min ago'
                        : diffMin < 60
                          ? `${diffMin} min ago`
                          : diffMin < 120
                            ? '1 hr ago'
                            : `${Math.floor(diffMin / 60)} hrs ago`;
                  return (
                    <span className="text-xs text-theme-text-tertiary">Last synced: {timeAgo}</span>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Import Stats */}
          {importedBets.length > 0 && (
            <div className="pt-4 border-t border-theme-border">
              <h4 className="font-medium text-theme-text-secondary mb-3">Import Summary</h4>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-3 bg-theme-surface rounded-lg">
                  <p className="text-2xl font-bold text-theme-text">{importedBets.length}</p>
                  <p className="text-sm text-theme-text-secondary">Total Imported</p>
                </div>
                <div className="p-3 bg-theme-surface rounded-lg">
                  <p className="text-2xl font-bold text-theme-success">
                    {importedBets.filter((b) => b.processed).length}
                  </p>
                  <p className="text-sm text-theme-text-secondary">Processed</p>
                </div>
                <div className="p-3 bg-theme-surface rounded-lg">
                  <p className={cn('text-2xl font-bold', 'text-theme-warning')}>
                    {importedBets.filter((b) => b.parlayId !== null).length}
                  </p>
                  <p className="text-sm text-theme-text-secondary">Linked</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
